import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useHistory } from 'react-router'
import { filter, propEq } from 'ramda'
import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { Button } from '@/components/button'
import { Reveal } from '@/components/animations'
import { confirm } from '@/components/dialog'
import {
  DashboardRow,
  DashboardContent,
  DashboardTitle,
  DashboardDescription
} from '@/components/settings'
import formatName from '@/utils/format-name'
import { isNullOrEmpty } from '@/utils'
import Icon from '@/components/icon'
import { ExpandIcon } from '@/components/expand'
import { Box } from '@/components/structure'
import {
  showDeletePadErrorModal,
  useBank,
  useAccountType,
  formatAccountNumberShort,
  formatTransitNumber,
  formatAccountNumber,
  PadInfo,
  Contract
} from '@/features/Pad'
import StatusBadge from '@/components/status-badge'
import { useDispatch } from '@/hooks/redux'
import { TrashIcon } from '@/components/icons'
import { InfoContainer, ItemWrapper, ItemList, ItemDescription, ItemValue } from './elements'

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 32px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    align-items: flex-start;
  }
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: ${colors.grey96};
  margin-right: 16px;
`

const StyledDashboardContent = styled(DashboardContent)`
  overflow: hidden;
  margin-right: 20px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-bottom: 0;
    text-align: left;
  }
`

const StyledDashboardTitle = styled(DashboardTitle)`
  display: block;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const WarningWrapper = styled.div`
  display: flex;
  > div:not(:last-child) {
    margin-right: 8px;
  }
`

type EditPadProps = {
  contracts: Contract[]
  accountData?: PadInfo
}

const onSuccess = () => {}

const EditPad: React.FC<EditPadProps> = ({ contracts, accountData }) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const dispatch = useDispatch()
  const history = useHistory()
  const [expanded, setExpanded] = useState(false)
  const personalInfo = accountData?.personal_info
  const financialInfo = accountData?.financial_info
  const bank = useBank(financialInfo?.financial_institution_name)
  const account = useAccountType(financialInfo?.account_type)
  const accountNumberShort = formatAccountNumberShort(financialInfo?.account_number)

  const connectedContracts = filter(propEq('payment_method', 'pad'), contracts)
  const hasConnectedContracts = !isNullOrEmpty(connectedContracts)

  // TODO: Add logic for failed/paused PAD payments once BE has setup
  const hasFailedContracts = false
  const hasPausedContracts = false
  const canDelete = !hasConnectedContracts

  const deletePad = async () => {
    if (!canDelete) {
      return showDeletePadErrorModal({ contracts: connectedContracts, history })
    }
    return confirm(
      t('l.payment_settings.edit.remove_pad_account_confirm.title', 'Delete Bank Account?'),
      t(
        'l.payment_settings.edit.remove_pad_account_confirm.text',
        'Are you sure you want to continue? You will no longer be able to opt-in to PAD (Pre-Authorized Debits) until a new account is connected.'
      ),
      {
        okLabel: t('common.delete', 'Delete'),
        cancelLabel: t('common.back', 'Back')
      }
    ).then(async () => {
      const response = await dispatch.settlements.deletePad()
      if (response.ok) {
        onSuccess()
      }
    })
  }

  return (
    <>
      <Row>
        <IconWrapper>
          <Icon id="document" height={17} width={17} />
        </IconWrapper>
        <StyledDashboardContent>
          <StyledDashboardTitle>{formatName(personalInfo)}</StyledDashboardTitle>
          <DashboardDescription>
            {displayMobile
              ? t('r.payment_settings.view.pad_account_number_mobile.text', {
                  defaultValue: 'Account ending in {{accountNumberShort}}',
                  accountNumberShort
                })
              : t('r.payment_settings.view.pad_account_number.text', {
                  defaultValue: 'Account number ending in {{accountNumberShort}}',
                  accountNumberShort
                })}
          </DashboardDescription>
          <WarningWrapper>
            {hasFailedContracts && (
              <Box top={12}>
                <StatusBadge
                  type="warning"
                  title={t('r.payment_settings.view.payment_failed.text', 'Payment Failed')}
                  tooltip={t(
                    'r.payment_settings.view.payment_failed_tooltip.text',
                    'We have detected that a payment failed to process. Please verify you have available funds or check your account information is still accurate.'
                  )}
                />
              </Box>
            )}
            {hasPausedContracts && (
              <Box top={12}>
                <StatusBadge
                  type="warning"
                  title={t('r.payment_settings.view.payment_paused.text', 'Payment Paused')}
                  tooltip={t(
                    'r.payment_settings.view.payment_paused_tooltip.text',
                    'Your automated rent payment is currently paused becuase you opted to Stop PAD. If you would like to resume automated rent payments, please click the Restart Payments button.'
                  )}
                />
              </Box>
            )}
          </WarningWrapper>
        </StyledDashboardContent>
        {!displayMobile && (
          <Box right={20}>
            <Button
              size="small"
              theme="secondary"
              to="/settings/payment-settings/pre-authorized-debit"
            >
              {t('common.edit', 'Edit')}
            </Button>
          </Box>
        )}
        <Button theme="secondary" shape="round" onClick={() => setExpanded(value => !value)}>
          <ExpandIcon open={expanded} />
        </Button>
      </Row>
      <Reveal isOpen={expanded} width="100%">
        <DashboardRow top="0">
          <InfoContainer>
            <ItemList>
              <ItemWrapper>
                <ItemDescription>
                  {t('r.payment_settings.view.edit.pad.account_holder.label', 'Account Holder')}
                </ItemDescription>
                <ItemValue>{formatName(personalInfo)}</ItemValue>
              </ItemWrapper>
              <ItemWrapper>
                <ItemDescription>
                  {t('r.payment_settings.view.edit.pad.transit_number.label', 'Branch Number')}
                </ItemDescription>
                <ItemValue>{formatTransitNumber(financialInfo?.transit_number)}</ItemValue>
              </ItemWrapper>
              <ItemWrapper>
                <ItemDescription>
                  {t('r.payment_settings.view.edit.pad.account_number.label', 'Account Number')}
                </ItemDescription>
                <ItemValue>{formatAccountNumber(financialInfo?.account_number)}</ItemValue>
              </ItemWrapper>
              <ItemWrapper>
                <ItemDescription>
                  {t(
                    'r.payment_settings.view.edit.pad.financial_institution_number.label',
                    'Financial Institution'
                  )}
                </ItemDescription>
                <ItemValue>{bank}</ItemValue>
              </ItemWrapper>
              <ItemWrapper>
                <ItemDescription>
                  {t('r.payment_settings.view.edit.pad.account_type.label', 'Account Type')}
                </ItemDescription>
                <ItemValue>{account}</ItemValue>
              </ItemWrapper>
            </ItemList>
          </InfoContainer>
        </DashboardRow>
        <DashboardRow top="0" bottom={displayMobile ? '16px' : '32px'}>
          <Button size="small" theme="delete" onClick={deletePad}>
            <Box pleft="2px" pright="2px" as="span">
              <TrashIcon />
            </Box>
            {t('common.remove', 'Remove')}
          </Button>
        </DashboardRow>
      </Reveal>
      {displayMobile && (
        <Box width="100%" pbottom={32} pleft={32} pright={32}>
          <Button
            width="100%"
            size="small"
            theme="secondary"
            to="/settings/payment-settings/pre-authorized-debit"
          >
            {t('common.edit', 'Edit')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default EditPad
